<template>
  <v-card outlined class="my-3" data-testid="manuelle-zahlungen">
    <v-card-title class="subtitle-1">Zahlung ohne Upload bestätigen</v-card-title>
    <v-card-text>
      <p class="text-caption">
        Tatsächlich {{ targetType === 'cost' ? 'bezahlten' : 'eingegangenen' }} Betrag und Datum eintragen.
        Die Zahlung wird dieser {{ targetType === 'cost' ? 'Kostenposition' : 'Rechnung' }} zugeordnet.
        Eine zusätzliche Belegprüfung ist nicht erforderlich.
        Eine bereits als Buchungsbeleg erfasste Zahlung bitte nicht noch einmal bestätigen.
      </p>
      <v-alert v-if="fehler" dense text type="error" role="alert">{{ fehler }}</v-alert>
      <v-alert v-if="erfolg" dense text type="success" role="status">{{ erfolg }}</v-alert>
      <v-btn small text :disabled="speichern" :loading="laden" @click="ladeStand">Zahlungen aktualisieren</v-btn>
      <template v-if="stand">
        <div v-if="stand.calculationMethod === 'document_balance'" class="my-2">
          Offen für diese Rechnung: {{ geld(stand.openAmountMinor) }}
          · Ohne Upload bestätigt: {{ geld(stand.manualPaidAmountMinor) }}
        </div>
        <div v-else class="my-2">Manuell zugeordnet: {{ geld(stand.manualPaidAmountMinor) }} · Bankzuordnung: {{ geld(stand.bankPaidAmountMinor) }}</div>
        <v-alert v-if="stand.conflicts && stand.conflicts.length" dense text type="warning">
          {{ stand.conflicts.map(konfliktText).join(' · ') }}
        </v-alert>
        <v-simple-table v-if="stand.items && stand.items.length" dense>
          <thead><tr><th>Datum</th><th>Betrag</th><th>Referenz</th><th>Status</th><th /></tr></thead>
          <tbody>
            <tr v-for="zahlung in stand.items" :key="zahlung.id">
              <td>{{ zahlung.paymentDate }}</td><td>{{ geld(zahlung.amountMinor) }}</td>
              <td>{{ zahlung.reference }}</td>
              <td>{{ zahlung.status === 'recorded' ? 'Erfasst' : 'Storniert' }}<span v-if="zahlung.stale"> · Zuordnung geändert</span></td>
              <td><v-btn v-if="zahlung.status === 'recorded'" small text :disabled="speichern || laden" @click="storno = zahlung; grund = ''">Stornieren</v-btn></td>
            </tr>
          </tbody>
        </v-simple-table>
        <template v-if="storno">
          <p class="mt-3">Zahlung vom {{ storno.paymentDate }} über {{ geld(storno.amountMinor) }} stornieren. Der Eintrag bleibt im Verlauf erhalten.</p>
          <v-text-field v-model="grund" label="Grund der Stornierung" maxlength="1000" :disabled="speichern" />
          <v-btn small color="warning" :loading="speichern" :disabled="!grund.trim() || laden" @click="storniere">Stornierung bestätigen</v-btn>
          <v-btn small text :disabled="speichern" @click="storno = null">Abbrechen</v-btn>
        </template>
        <template v-else>
          <v-text-field v-model="betrag" label="Betrag in EUR" inputmode="decimal" hint="Zum Beispiel 357,00" :disabled="speichern" />
          <v-text-field v-model="datum" label="Zahlungsdatum" type="date" :max="heute" :disabled="speichern" />
          <v-text-field v-model="referenz" label="Referenz / Vermerk (optional)" maxlength="255" :disabled="speichern" />
          <v-checkbox v-model="bestaetigt" :disabled="speichern" label="Ich bestätige, dass dieser Betrag bezahlt wurde und noch nicht als Buchungsbeleg oder Zahlung erfasst ist." />
          <v-btn small color="primary" :loading="speichern" :disabled="!gueltig || laden" @click="erfasse">Zahlung bestätigen</v-btn>
        </template>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import $ from 'jquery';
import { sichereApiFehlermeldung } from '@/utils/apiFehler';
import { zahlungsbetragInCent, zahlungsdatumHeute } from '@/utils/manuelleZahlung';

export default {
  props: {
    targetType: { type: String, required: true },
    targetId: { type: [Number, String], required: true },
    refreshKey: { type: Number, default: 0 },
  },
  data: () => ({
    stand: null, betrag: '', datum: zahlungsdatumHeute(), referenz: '', bestaetigt: false,
    laden: false, speichern: false, fehler: '', erfolg: '', storno: null, grund: '',
    sequenz: 0, zielSequenz: 0, wiederholung: null,
  }),
  computed: {
    heute() { return zahlungsdatumHeute(); },
    gueltig() {
      return !!this.stand && !!this.stand.targetVersion && this.bestaetigt
        && zahlungsbetragInCent(this.betrag) !== null && /^\d{4}-\d{2}-\d{2}$/.test(this.datum) && this.datum <= this.heute;
    },
  },
  watch: {
    targetId() { this.zielWechsel(); },
    targetType() { this.zielWechsel(); },
    refreshKey() { this.ladeStand(); },
  },
  methods: {
    geld(cent) { return (Number(cent || 0) / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }); },
    konfliktText(code) {
      return ({ manual_bank_overlap: 'Bankzuordnung und manuelle Erfassung überschneiden sich. Den doppelten manuellen Eintrag bitte stornieren.',
        manual_payment_target_changed: 'Die zugehörige Rechnung oder Kostenposition wurde geändert. Zahlung bitte prüfen.',
        overpayment: 'Der erfasste Betrag übersteigt den Rechnungsbetrag.' })[code] || code;
    },
    zielWechsel() {
      this.sequenz += 1;
      this.zielSequenz += 1;
      this.stand = null;
      this.erfolg = '';
      this.fehler = '';
      this.betrag = '';
      this.referenz = '';
      this.bestaetigt = false;
      this.storno = null;
      this.wiederholung = null;
      this.ladeStand();
    },
    anfrage(payload) {
      return $.ajax({
        url: `${process.env.VUE_APP_API_HOST}/400/410/`, type: 'post', dataType: 'json', timeout: 20000,
        data: { code: this.$store.state.login.session.code, dataToSave: JSON.stringify(payload) },
      });
    },
    uebernehmen(response, eigenesSpeichern = false) {
      if (response && response.status === 'accessError') this.$store.commit('login/backendErrorSession');
      if (!response || response.status !== 'success' || !response.manualPayments) {
        this.fehler = sichereApiFehlermeldung(response && response.message, 'Die Zahlungsdaten konnten nicht verarbeitet werden.');
        return false;
      }
      this.stand = response.manualPayments;
      // Nur eigene bestaetigte Schreibvorgaenge aktualisieren die Version des offenen Auftrags.
      if (eigenesSpeichern) this.$emit('changed', response);
      return true;
    },
    async ladeStand() {
      if (this.speichern) return;
      const sequenz = ++this.sequenz;
      this.laden = true;
      this.fehler = '';
      try {
        const response = await this.anfrage({ type: 'getManualPayments', targetType: this.targetType, targetId: Number(this.targetId) });
        if (sequenz === this.sequenz) this.uebernehmen(response);
      } catch (error) {
        if (sequenz === this.sequenz) this.fehler = 'Zahlungen konnten nicht geladen werden. Bitte erneut versuchen.';
      } finally {
        if (sequenz === this.sequenz) this.laden = false;
      }
    },
    erfasse() {
      if (!this.gueltig) return;
      this.sende({ type: 'addManualPayment', targetVersion: this.stand.targetVersion,
        paymentDate: this.datum, amountMinor: zahlungsbetragInCent(this.betrag), reference: this.referenz.trim() });
    },
    storniere() {
      if (!this.storno || this.storno.status !== 'recorded' || !this.grund.trim()) return;
      this.sende({ type: 'revokeManualPayment', paymentId: this.storno.id, rowVersion: this.storno.rowVersion, reason: this.grund.trim() });
    },
    formularZuruecksetzen() {
      this.betrag = '';
      this.referenz = '';
      this.bestaetigt = false;
      this.storno = null;
      this.wiederholung = null;
    },
    async sende(aktion) {
      if (this.speichern || this.laden) return;
      const payload = { ...aktion, targetType: this.targetType, targetId: Number(this.targetId) };
      const signatur = JSON.stringify(payload);
      if (!this.wiederholung || this.wiederholung.signatur !== signatur) {
        this.wiederholung = { signatur, key: `payment-${Date.now()}-${Math.random().toString(36).slice(2)}` };
      }
      payload.idempotencyKey = this.wiederholung.key;
      const ziel = `${this.targetType}:${this.targetId}`;
      const zielSequenz = this.zielSequenz;
      const zielAktuell = () => zielSequenz === this.zielSequenz
        && ziel === `${this.targetType}:${this.targetId}`;
      this.speichern = true;
      this.fehler = '';
      this.erfolg = '';
      let neuLaden = false;
      try {
        const response = await this.anfrage(payload);
        if (!zielAktuell()) return;
        if (response && response.idempotentReplay) {
          neuLaden = true;
          this.formularZuruecksetzen();
          this.erfolg = 'Diese Zahlung wurde bereits verarbeitet. Der aktuelle Stand wird geladen.'
            + (this.targetType === 'order_document' ? ' Vor dem weiteren Speichern den Auftrag neu öffnen.' : '');
        } else if (this.uebernehmen(response, true)) {
          this.erfolg = aktion.type === 'addManualPayment' ? 'Zahlung gespeichert – ohne PDF.' : 'Zahlung storniert.';
          this.formularZuruecksetzen();
        } else if (response && response.status === 'conflict') neuLaden = true;
      } catch (error) {
        if (!zielAktuell()) return;
        this.fehler = sichereApiFehlermeldung(error.responseJSON && error.responseJSON.message,
          'Keine eindeutige Serverantwort. Erneutes Senden desselben Vorgangs erzeugt keine doppelte Zahlung.');
        neuLaden = error.status === 409;
      } finally {
        this.speichern = false;
        if (!zielAktuell()) this.ladeStand();
      }
      if (neuLaden) {
        const meldung = this.fehler;
        await this.ladeStand();
        if (meldung) this.fehler = `${meldung} Der aktuelle Stand wurde geladen. Bitte prüfen.`;
      }
    },
  },
  created() { this.ladeStand(); },
};
</script>
